import { Map, OrderedMap } from 'immutable';
import { createSelector } from '@reduxjs/toolkit';

import type { ReduxState } from 'app/redux/types';

import type { BettingEntitiesSelectors } from 'app/lib/betting/betting-entities';
import { orderByGlobalPriority } from 'app/lib/grid';
import { currentLocation } from 'app/modules/Navigation/selectors';

const updateMatchIds = (matchIds = new Set(), ids: Set<number> | number) => {
  if (typeof ids === 'number') {
    return new Set([...matchIds, ids]);
  }
  return new Set([...matchIds, ...ids]);
};

export default (
  tournaments: BettingEntitiesSelectors['tournaments'],
  matches: BettingEntitiesSelectors['matches'],
  favoritesMatchIdsSelector: (state: ReduxState) => Set<number>,
) => {
  /**
   * Create "favorites by tournamentIds grid entities" selector for `Favorites` grid.
   */

  return createSelector(
    tournaments,
    matches,
    favoritesMatchIdsSelector,
    currentLocation,
    (tournamentsById, matchesById, matchIds, { tournamentId, sportId }) => {
      const sortedByStartTime = orderByGlobalPriority(matchesById, [...matchIds]);

      if (tournamentId && sportId) {
        const tournament = tournamentsById.get(tournamentId);

        if (!tournament) {
          return Map();
        }

        return Map([
          [sportId, Map([
            [tournamentId, tournament?.matchIds || new Set()],
          ])],
        ]);
      }

      return (sortedByStartTime).reduce((map, id) => {
        const tournament = tournamentsById.get(id);
        const match = matchesById.get(id);

        if (tournament) {
          const tournamentMachIds = map.get(tournament.sportId)?.get(tournament.id);

          return map.setIn(
            [tournament.sportId, tournament.id],
            updateMatchIds(tournamentMachIds, tournament.matchIds),
          );
        }

        if (match) {
          const matchIds = map.get(match.sportId)?.get(match.tournamentId);

          return map.setIn(
            [match.sportId, match.tournamentId],
            updateMatchIds(matchIds, id),
          );
        }

        // if no tournament no match is found

        return map;
      }, OrderedMap<number, Map<number, Set<number>>>());
    },
  );
};
